import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import mapboxgl from 'mapbox-gl';
import { Filter, MapPin } from 'lucide-react';
import type { Business } from '@shared/schema';
import { businessCategories } from '@shared/schema';

export default function MapPage() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [mapLoaded, setMapLoaded] = useState(false);

  const { data: businesses = [], isLoading } = useQuery({
    queryKey: ['/api/businesses', { approved: true, category: selectedCategory, city: selectedCity }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append('approved', 'true');
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedCity) params.append('city', selectedCity);
      return fetch(`/api/businesses?${params.toString()}`).then(res => res.json()) as Promise<Business[]>
    }
  });

  const { data: scrapedBusinesses = [] } = useQuery({
    queryKey: ['/api/scraped-businesses'],
    queryFn: () => fetch('/api/scraped-businesses').then(res => res.json())
  });

  const allBusinesses = [...businesses, ...scrapedBusinesses];

  // Get unique cities for filter
  const cities = Array.from(new Set(allBusinesses.map(b => b.city))).sort();

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiZXhvcmlvbiIsImEiOiJjbWRkcXRuN2UwN2E4Mm1wdGZsMnpvaTR6In0.9K5ab5tAiWBCWxid8WdLfg';
    
    console.log('Mapbox token found:', mapboxToken ? 'Yes' : 'No');
    console.log('Token length:', mapboxToken?.length);
    
    if (!mapboxToken) {
      // Show error message if no token
      const mapElement = mapContainer.current;
      mapElement.innerHTML = `
        <div class="w-full h-full bg-gray-200 flex items-center justify-center">
          <div class="text-center">
            <div class="mx-auto mb-4 text-gray-400 w-12 h-12 flex items-center justify-center">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-700 mb-2">Map Unavailable</h3>
            <p class="text-gray-600">Mapbox API key required</p>
          </div>
        </div>
      `;
      return;
    }

    // Set Mapbox access token
    mapboxgl.accessToken = mapboxToken;

    try {
      // Initialize map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-95.7129, 37.0902], // Center of US
        zoom: 4
      });

      map.current.on('load', () => {
        console.log('Map loaded successfully');
        setMapLoaded(true);
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e);
        const mapElement = mapContainer.current;
        if (mapElement) {
          mapElement.innerHTML = `
            <div class="w-full h-full bg-red-100 flex items-center justify-center">
              <div class="text-center">
                <h3 class="text-xl font-semibold text-red-700 mb-2">Map Error</h3>
                <p class="text-red-600">Failed to load map. Check API key.</p>
              </div>
            </div>
          `;
        }
      });
    } catch (error) {
      console.error('Failed to initialize map:', error);
      const mapElement = mapContainer.current;
      if (mapElement) {
        mapElement.innerHTML = `
          <div class="w-full h-full bg-red-100 flex items-center justify-center">
            <div class="text-center">
              <h3 class="text-xl font-semibold text-red-700 mb-2">Map Initialization Error</h3>
              <p class="text-red-600">${error instanceof Error ? error.message : 'Unknown error'}</p>
            </div>
          </div>
        `;
      }
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Add business markers when map loads and businesses data changes
  useEffect(() => {
    if (!map.current || !mapLoaded || allBusinesses.length === 0) return;

    // Clear existing markers
    const existingMarkers = document.querySelectorAll('.mapbox-marker');
    existingMarkers.forEach(marker => marker.remove());

    // Add markers for each business
    allBusinesses.forEach((business) => {
      if (business.latitude && business.longitude) {
        // Create marker element
        const markerElement = document.createElement('div');
        markerElement.className = 'mapbox-marker bg-brand-red text-white p-2 rounded-full text-xs font-bold cursor-pointer hover:bg-red-700 transition-colors shadow-lg';
        markerElement.style.width = '40px';
        markerElement.style.height = '40px';
        markerElement.style.display = 'flex';
        markerElement.style.alignItems = 'center';
        markerElement.style.justifyContent = 'center';
        markerElement.textContent = business.name.charAt(0);

        // Create popup
        const popup = new mapboxgl.Popup({
          offset: 25,
          closeButton: true,
          closeOnClick: false
        });

        // Create popup content safely
        const popupContent = document.createElement('div');
        popupContent.className = 'p-2';

        const title = document.createElement('h4');
        title.className = 'font-bold text-gray-900 mb-1';
        title.textContent = business.name;

        const category = document.createElement('p');
        category.className = 'text-sm text-gray-600 mb-1';
        category.textContent = business.category;

        const location = document.createElement('p');
        location.className = 'text-sm text-gray-500 mb-2';
        location.textContent = `${business.city}, ${business.state}`;

        const rating = document.createElement('div');
        rating.className = 'flex items-center text-sm';
        rating.innerHTML = `<span class="text-yellow-500">★</span> <span class="ml-1">${business.rating > 0 ? business.rating.toFixed(1) : 'No reviews'}</span>`;

        const viewButton = document.createElement('a');
        viewButton.href = `/business/${encodeURIComponent(business.id)}`;
        viewButton.className = 'inline-block mt-2 px-3 py-1 bg-brand-red text-white text-xs rounded hover:bg-red-700 transition-colors';
        viewButton.textContent = 'View Details';

        popupContent.appendChild(title);
        popupContent.appendChild(category);
        popupContent.appendChild(location);
        popupContent.appendChild(rating);
        popupContent.appendChild(viewButton);

        popup.setDOMContent(popupContent);

        // Create marker
        new mapboxgl.Marker(markerElement)
          .setLngLat([business.longitude, business.latitude])
          .setPopup(popup)
          .addTo(map.current!);
      }
    });
  }, [allBusinesses, mapLoaded]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Business Map</h1>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <Filter size={20} className="text-gray-600" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
              >
                <option value="">All Categories</option>
                {businessCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <MapPin size={20} className="text-gray-600" />
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
              >
                <option value="">All Cities</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            
            <div className="text-sm text-gray-600 flex items-center">
              Showing {allBusinesses.length} businesses
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="h-[calc(100vh-200px)]">
        {isLoading ? (
          <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading map...</p>
            </div>
          </div>
        ) : (
          <div ref={mapContainer} className="w-full h-full" />
        )}
      </div>
    </div>
  );
}